import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Heart, MessageCircle, Share2, ShoppingBag, UserPlus, UserCheck, Volume2, VolumeX, Play, Pause, Gift, Trophy, Languages } from "lucide-react";
import { toast } from "sonner";
import GiftDrawer from "./GiftDrawer";

// Filter codes accepted by the backend; mirrored from PlyxUpload.jsx so the
// player styles match exactly what the creator picked.
const BEAUTIFY_CSS = {
  none:       "none",
  // Originals
  vivid:      "saturate(1.4) contrast(1.1)",
  warm:       "sepia(0.18) saturate(1.2) hue-rotate(-8deg)",
  cool:       "saturate(1.1) hue-rotate(8deg) brightness(1.02)",
  bw:         "grayscale(1) contrast(1.05)",
  vintage:    "sepia(0.5) saturate(0.85) contrast(0.95)",
  dreamy:     "blur(0.4px) saturate(1.15) brightness(1.05) contrast(0.95)",
  // Face-beauty v2
  smooth:     "blur(0.7px) saturate(1.1) brightness(1.06) contrast(0.96)",
  glow:       "brightness(1.12) saturate(1.18) contrast(1.05) hue-rotate(-4deg)",
  porcelain:  "brightness(1.1) saturate(0.9) contrast(1.02) hue-rotate(4deg)",
  kbeauty:    "brightness(1.08) saturate(1.05) contrast(0.94) sepia(0.06)",
  cinematic:  "contrast(1.18) saturate(0.92) brightness(0.97)",
  sunset:     "sepia(0.25) saturate(1.25) brightness(1.05) hue-rotate(-12deg)",
  golden:     "sepia(0.45) saturate(1.3) brightness(1.06)",
  fresh:      "brightness(1.1) saturate(1.3) contrast(1.04)",
  midnight:   "brightness(0.92) saturate(1.05) contrast(1.1) hue-rotate(14deg)",
  drama:      "contrast(1.3) saturate(0.7) brightness(0.96)",
};

const fmtCount = (n) => {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const currencySymbol = (c) => ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$" }[c] || `${c} `);

// Show the "you've maxed out your watch-to-earn cap" toast at most once per
// session per cap period (so the user isn't nagged on every subsequent video
// once they hit the limit). Daily caps reset by date string, monthly by month.
const maybeShowCapToast = (capHit) => {
  try {
    const period = capHit === "monthly"
      ? new Date().toISOString().slice(0, 7)   // YYYY-MM
      : new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const key = `plyx_w2e_cap_toast_${capHit}_${period}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    if (capHit === "monthly") {
      toast(
        "You've hit your £40 monthly watch-to-earn cap — your balance is safe and resets next month.",
        { duration: 5000, icon: "🏆" }
      );
    } else if (capHit === "daily") {
      toast(
        "You've hit today's watch-to-earn cap — come back tomorrow to earn more.",
        { duration: 4000, icon: "⏳" }
      );
    }
  } catch (_e) { /* sessionStorage can fail in private mode — silently ignore */ }
};

// Predictive nudge: once a viewer crosses 90% of their monthly cap, show a
// single "you're close" toast so the cap-hit moment isn't a surprise. Fires
// at most once per calendar month per session.
const maybeShowNearCapToast = (earnedMonth, monthlyCap) => {
  if (typeof earnedMonth !== "number" || typeof monthlyCap !== "number" || monthlyCap <= 0) return;
  if (earnedMonth / monthlyCap < 0.9) return;
  try {
    const period = new Date().toISOString().slice(0, 7); // YYYY-MM
    const key = `plyx_w2e_near_cap_toast_${period}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const left = Math.max(0, monthlyCap - earnedMonth);
    toast(
      `You're close — only £${left.toFixed(2)} left in this month's watch-to-earn cap.`,
      { duration: 4500, icon: "⚡" }
    );
  } catch (_e) { /* ignore */ }
};

/**
 * Vertical full-screen video card for Plyx feed.
 * Auto-plays when in view, pauses when out. Tap to mute/pause, swipe nav done by parent.
 */
const VideoCard = ({ video, isActive, onLikeToggle, onCommentClick, onFollowToggle }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(!!video.liked_by_me);
  const [likeCount, setLikeCount] = useState(video.like_count || 0);
  const [followed, setFollowed] = useState(!!video.followed_by_me);
  const [progress, setProgress] = useState(0);   // 0..1, drives the bottom scrubber
  const [heartBurst, setHeartBurst] = useState(0); // re-keys the burst element for replay
  const [giftDrawerOpen, setGiftDrawerOpen] = useState(false);
  const [floatingGifts, setFloatingGifts] = useState([]); // [{id, emoji, coins}]
  const [giftStats, setGiftStats] = useState({ total: 0 });
  const [captionsOn, setCaptionsOn] = useState(true);
  const [shopSheetOpen, setShopSheetOpen] = useState(false);
  const [captionLine, setCaptionLine] = useState("");
  const [captionLang, setCaptionLang] = useState("auto");      // "auto" = original, else lang code
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [translatedSegs, setTranslatedSegs] = useState(null);  // overrides captionSegs when set
  const tapTimerRef = useRef(null);
  const { user: me } = usePlyxAuth();
  const navigate = useNavigate();
  const viewedRef = useRef(false);

  // Sync local state if parent refreshes video object
  useEffect(() => {
    setLiked(!!video.liked_by_me);
    setLikeCount(video.like_count || 0);
    setFollowed(!!video.followed_by_me);
  }, [video.video_id, video.liked_by_me, video.like_count, video.followed_by_me]);

  // Pull recent gift stats once when the card becomes active.
  useEffect(() => {
    if (!isActive) return;
    let alive = true;
    (async () => {
      try {
        const r = await plyxApi.get(`/plyx/videos/${video.video_id}/gifts?limit=8`);
        if (!alive) return;
        setGiftStats({
          total: r.data.total_gifts || 0,
          topGifters: r.data.top_gifters || [],
        });
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [isActive, video.video_id]);

  // Whisper auto-captions — fetched lazily once per active video.
  const [captionSegs, setCaptionSegs] = useState(null);
  useEffect(() => {
    if (!isActive) { setCaptionSegs(null); setCaptionLine(""); setTranslatedSegs(null); setCaptionLang("auto"); return; }
    if (video.captions_status !== "ready") return;
    let alive = true;
    (async () => {
      try {
        const r = await plyxApi.get(`/plyx/videos/${video.video_id}/captions`);
        if (!alive) return;
        setCaptionSegs(r.data?.segments || []);
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [isActive, video.video_id, video.captions_status]);

  const handleGiftSent = ({ emoji, coins }) => {
    // Spawn a floating emoji that rises and fades out — pure CSS keyframes.
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const lane = Math.random() * 60 + 20;   // horizontal lane, % from left
    const size = 28 + Math.min(60, Math.floor(coins / 100) * 4);  // bigger emoji for bigger gifts
    setFloatingGifts((arr) => [...arr, { id, emoji, lane, size }]);
    setGiftStats((s) => ({ ...s, total: (s.total || 0) + 1 }));
    setTimeout(() => setFloatingGifts((arr) => arr.filter((g) => g.id !== id)), 3200);
    // Refresh top-gifters so the ribbon reflects this send within ~1s.
    plyxApi.get(`/plyx/videos/${video.video_id}/gifts?limit=1`)
      .then((r) => setGiftStats((s) => ({ ...s, topGifters: r.data.top_gifters || s.topGifters })))
      .catch(() => { /* silent */ });
  };


  // Auto play/pause based on whether this card is the visible one.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Respect optional trim_start / trim_end metadata so the player loops
    // only the section the creator chose during upload.
    const tStart = (typeof video.trim_start === "number" && video.trim_start >= 0) ? video.trim_start : 0;
    const tEnd = (typeof video.trim_end === "number" && video.trim_end > tStart) ? video.trim_end : null;
    if (isActive) {
      try { v.currentTime = tStart; } catch { /* ignore */ }
      const p = v.play();
      if (p && typeof p.then === "function") p.then(() => setPlaying(true)).catch(() => setPlaying(false));
      viewedRef.current = false;  // reset for next view
      // Send the view ping when the user has watched ≥ 5s, with watched_sec
      // so the backend can award watch-to-earn credit.
      const sendView = () => {
        if (viewedRef.current) return;
        viewedRef.current = true;
        const watched = Math.max(0, v.currentTime || 0);
        plyxApi.post(`/plyx/videos/${video.video_id}/view`, { watched_sec: watched })
          .then((r) => {
            if (r?.data?.credit_earned && r?.data?.credit?.amount) {
              toast.success(`+£${r.data.credit.amount.toFixed(2)} earned`, { duration: 2000 });
              maybeShowNearCapToast(r.data.earned_month, r.data.monthly_cap);
            } else if (r?.data?.cap_hit) {
              maybeShowCapToast(r.data.cap_hit);
            }
          })
          .catch(() => { /* fire-and-forget */ });
      };
      // Either at 5s or when user pauses / scrolls off, whichever first.
      const onTime = () => {
        if ((v.currentTime || 0) >= 5) sendView();
        // Loop back to trim_start once we cross trim_end.
        if (tEnd != null && v.currentTime >= tEnd) {
          try { v.currentTime = tStart; } catch { /* ignore */ }
        }
        // Update scrubber progress (0..1) — relative to the trimmed window
        // so the bar fills end-to-end inside the clip the creator wanted.
        const dur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
        const total = tEnd != null ? Math.max(0.01, tEnd - tStart) : dur;
        const rel = Math.max(0, (v.currentTime || 0) - tStart);
        if (total > 0) setProgress(Math.min(1, rel / total));
        // Caption line — pick the segment whose [start,end] contains currentTime.
        if (captionsOn && Array.isArray(captionSegs) && captionSegs.length > 0) {
          const segs = translatedSegs || captionSegs;
          const t = v.currentTime || 0;
          const seg = segs.find((s) => t >= s.start && t <= s.end + 0.25);
          setCaptionLine(seg ? seg.text : "");
        } else if (captionLine) {
          setCaptionLine("");
        }
      };
      v.addEventListener("timeupdate", onTime);
      return () => {
        v.removeEventListener("timeupdate", onTime);
        sendView();  // also fire on unmount/scroll-off if at least started
      };
    } else {
      v.pause();
      setPlaying(false);
      setProgress(0);
    }
  }, [isActive, video.video_id, video.trim_start, video.trim_end, captionsOn, captionSegs, translatedSegs, captionLine]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  // Single-tap = play/pause, Double-tap = like + heart burst (TikTok pattern).
  // We delay the single-tap action by 250ms; if a second tap arrives, it cancels
  // the play-toggle and triggers the like instead.
  const handleVideoTap = () => {
    if (tapTimerRef.current) {
      // Double-tap detected
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      setHeartBurst((n) => n + 1);
      if (!liked) {
        // Optimistic like (re-uses handleLike's logic via a force flag)
        if (!me) { navigate("/plyx/login"); return; }
        setLiked(true);
        setLikeCount((c) => c + 1);
        plyxApi.post(`/plyx/videos/${video.video_id}/like`)
          .then(() => onLikeToggle?.(video.video_id, true))
          .catch(() => {
            setLiked(false);
            setLikeCount((c) => c - 1);
            toast.error("Could not update like");
          });
      }
      return;
    }
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null;
      togglePlay();
    }, 250);
  };

  const handleLike = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    try {
      if (next) await plyxApi.post(`/plyx/videos/${video.video_id}/like`);
      else await plyxApi.delete(`/plyx/videos/${video.video_id}/like`);
      onLikeToggle?.(video.video_id, next);
    } catch {
      // revert
      setLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
      toast.error("Could not update like");
    }
  };

  const handleFollow = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    if (!video.creator?.handle) return;
    const next = !followed;
    setFollowed(next);
    try {
      if (next) await plyxApi.post(`/plyx/u/${video.creator.handle}/follow`);
      else await plyxApi.delete(`/plyx/u/${video.creator.handle}/follow`);
      onFollowToggle?.(video.creator.plyx_user_id, next);
    } catch {
      setFollowed(!next);
      toast.error("Could not update follow");
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/plyx/v/${video.video_id}`;
    plyxApi.post(`/plyx/videos/${video.video_id}/share`).catch(() => {});
    if (navigator.share) {
      try {
        await navigator.share({ title: video.caption?.slice(0, 60) || "Watch on Plyx", url });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      } catch {
        toast.message(url);
      }
    }
  };

  const listing = video.listing;
  const isMine = me && video.creator?.plyx_user_id === me.plyx_user_id;

  return (
    <div className="relative w-full h-full bg-black overflow-hidden snap-start" data-testid={`plyx-video-card-${video.video_id}`}>
      {/* Media — video player or static photo with Ken-Burns zoom */}
      {video.media_type === "photo" ? (
        <img
          src={plyxMediaUrl(video.video_url)}
          alt={video.caption || "Plyx post"}
          className="w-full h-full object-contain bg-black cursor-pointer select-none"
          onClick={handleVideoTap}
          data-testid={`plyx-photo-${video.video_id}`}
          style={{
            filter: BEAUTIFY_CSS[video.beautify_filter] || "none",
            animation: isActive ? "plyxPhotoKenBurns 6s ease-in-out both" : "none",
          }}
        />
      ) : (
        <video
          ref={videoRef}
          src={plyxMediaUrl(video.video_url)}
          poster={plyxMediaUrl(video.thumbnail_url)}
          className="w-full h-full object-contain bg-black cursor-pointer select-none"
          loop
          playsInline
          muted={muted}
          onClick={handleVideoTap}
          data-testid={`plyx-video-${video.video_id}`}
          style={{ filter: BEAUTIFY_CSS[video.beautify_filter] || "none" }}
        />
      )}

      {/* iOS optimisation indicator — webm sources need ffmpeg to land
          before iPhone Safari can play them. We show a soft overlay
          while transcode is pending so users aren't confused. */}
      {video.media_type !== "photo"
        && video.ios_optimised === false
        && (video.transcode_status === "queued" || video.transcode_status === "running") && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[11px] text-white/90 inline-flex items-center gap-2 animate-pulse"
          data-testid={`plyx-video-optimising-${video.video_id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFB42D]"/> Optimising for iPhone playback…
        </div>
      )}

      {/* Creator emoji sticker overlay — positioned to match the upload editor */}
      {Array.isArray(video.stickers) && video.stickers.length > 0 && (
        <div className="absolute inset-0 pointer-events-none" data-testid={`plyx-video-stickers-${video.video_id}`}>
          {video.stickers.map((s, i) => (
            <span
              key={i}
              className="absolute select-none"
              style={{
                left: `${(s.x ?? 0.5) * 100}%`,
                top: `${(s.y ?? 0.5) * 100}%`,
                fontSize: s.size || 48,
                transform: `translate(-50%, -50%) rotate(${s.rot || 0}deg)`,
                textShadow: "0 0 8px rgba(0,0,0,0.4)",
              }}
            >
              {s.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Double-tap heart burst — fires once per double-tap, key forces re-mount */}
      {heartBurst > 0 && (
        <div
          key={heartBurst}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          data-testid={`plyx-heart-burst-${video.video_id}`}
        >
          <Heart
            className="w-32 h-32 fill-[#FF2D7A] text-[#FF2D7A] drop-shadow-2xl"
            style={{
              animation: "plyxHeartBurst 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards",
            }}
          />
        </div>
      )}

      {/* Play indicator overlay when paused */}
      {!playing && (
        <button
          type="button"
          onClick={togglePlay}
          aria-label="Play"
          className="absolute inset-0 flex items-center justify-center text-white/80 hover:text-white"
        >
          <span className="w-20 h-20 rounded-full bg-black/40 backdrop-blur flex items-center justify-center">
            <Play className="w-10 h-10 fill-white ml-1"/>
          </span>
        </button>
      )}

      {/* Top-right mute toggle */}
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        aria-label={muted ? "Unmute" : "Mute"}
        data-testid={`plyx-mute-${video.video_id}`}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center hover:bg-black/60"
      >
        {muted ? <VolumeX className="w-5 h-5"/> : <Volume2 className="w-5 h-5"/>}
      </button>

      {/* Top Gifters ribbon — fades in once the active video has gifts */}
      {isActive && (giftStats.topGifters || []).length > 0 && (
        <div
          className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur border border-white/10"
          style={{ animation: "plyxRibbonIn 500ms cubic-bezier(0.22,1,0.36,1) both" }}
          data-testid={`plyx-top-gifters-${video.video_id}`}
        >
          <Trophy className="w-3.5 h-3.5 text-[#FFB42D]" />
          <span className="text-[10px] uppercase tracking-wider text-white/70">Top gifters</span>
          <div className="flex items-center -space-x-1.5 ml-1">
            {(giftStats.topGifters || []).slice(0, 3).map((g, i) => (
              <Link
                key={g.handle + i}
                to={`/plyx/u/${g.handle}`}
                title={`@${g.handle} · ${g.coins_total} coins`}
                data-testid={`plyx-top-gifter-${video.video_id}-${i}`}
                className="relative w-7 h-7 rounded-full bg-white/15 border border-white/30 overflow-hidden flex items-center justify-center text-[11px] font-semibold hover:scale-110 transition-transform"
                style={{ zIndex: 10 - i }}
              >
                {g.avatar_url ? (
                  <img src={g.avatar_url} alt={g.handle} className="w-full h-full object-cover" />
                ) : (
                  <span>{(g.display_name || g.handle || "?").charAt(0).toUpperCase()}</span>
                )}
                <span
                  className="absolute -bottom-0.5 -right-0.5 text-[10px]"
                  style={{ textShadow: "0 0 4px rgba(0,0,0,0.6)" }}
                >
                  {g.top_gift_emoji}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Bottom-left: creator + caption + listing */}
      <div className="absolute bottom-0 left-0 right-16 p-4 sm:p-6 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white">
        {video.creator && (
          <div className="flex items-center gap-3 mb-2">
            <Link to={`/plyx/u/${video.creator.handle}`} className="flex items-center gap-2 hover:opacity-80" data-testid={`plyx-creator-link-${video.video_id}`}>
              <div className="w-9 h-9 rounded-full bg-white/15 overflow-hidden flex items-center justify-center text-white font-semibold">
                {video.creator.avatar_url
                  ? <img src={plyxMediaUrl(video.creator.avatar_url)} alt={video.creator.handle} className="w-full h-full object-cover"/>
                  : (video.creator.display_name?.slice(0, 1) || "P")}
              </div>
              <div>
                <div className="font-semibold text-sm">@{video.creator.handle}</div>
                {video.creator.country && <div className="text-xs text-white/60">{video.creator.country}</div>}
              </div>
            </Link>
            {!isMine && (
              <button
                type="button"
                onClick={handleFollow}
                data-testid={`plyx-follow-${video.video_id}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  followed ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-[#0A0A0A] hover:bg-white/90"
                }`}
              >
                {followed ? "Following" : "Follow"}
              </button>
            )}
          </div>
        )}
        {video.caption && (
          <p className="text-sm leading-relaxed max-w-md whitespace-pre-wrap line-clamp-3" data-testid={`plyx-caption-${video.video_id}`}>
            {video.caption}
          </p>
        )}
        {video.tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {video.tags.slice(0, 5).map((t) => (
              <span key={t} className="text-xs text-white/70">#{t}</span>
            ))}
          </div>
        )}
        {listing && (
          <Link
            to={`/listing/${listing.listing_id}?plyx_v=${video.video_id}`}
            data-testid={`plyx-buy-${video.video_id}`}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-white/90 transition-colors"
          >
            <ShoppingBag className="w-4 h-4"/>
            Shop · {listing.images?.[0] ? <img src={listing.images[0]} alt="" className="w-5 h-5 rounded object-cover"/> : null}
            <span className="truncate max-w-[140px]">{listing.title}</span>
            <span className="font-bold">{currencySymbol(listing.currency)}{listing.price}</span>
          </Link>
        )}
        {video.tagged_listings?.length > 0 && (
          <button
            type="button"
            onClick={() => setShopSheetOpen(true)}
            data-testid={`plyx-shop-chip-${video.video_id}`}
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FF2D7A] to-[#FFB42D] text-[#0A0A0A] text-xs font-bold hover:scale-105 transition-transform"
          >
            <ShoppingBag className="w-3.5 h-3.5"/>
            Shop {video.tagged_listings.length} item{video.tagged_listings.length === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {/* Tag-and-Sell bottom sheet */}
      {shopSheetOpen && video.tagged_listings?.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end justify-center" onClick={() => setShopSheetOpen(false)} data-testid={`plyx-shop-sheet-${video.video_id}`}>
          <div
            className="w-full max-w-md bg-[#0A0A0A] border-t border-white/10 rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#FFB42D]"/> Shop this clip
              </h3>
              <button
                type="button"
                onClick={() => setShopSheetOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                data-testid={`plyx-shop-sheet-close-${video.video_id}`}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {video.tagged_listings.map((p) => (
                <a
                  key={p.listing_id}
                  href={p.listing_url}
                  target="_top"
                  data-testid={`plyx-shop-sheet-item-${p.listing_id}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFB42D] transition-colors"
                >
                  <div className="w-16 h-16 rounded-lg bg-black/50 overflow-hidden shrink-0">
                    {p.thumbnail_url
                      ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy"/>
                      : <div className="w-full h-full flex items-center justify-center text-white/20"><ShoppingBag className="w-6 h-6"/></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate text-white">{p.title}</div>
                    <div className="text-base font-display text-[#FFB42D] mt-0.5 tabular-nums">
                      {currencySymbol(p.currency)}{p.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    {!p.is_buyable && <div className="text-[10px] text-red-400 uppercase tracking-wider">Unavailable</div>}
                  </div>
                  <div className="px-3 py-1.5 rounded-full bg-white text-[#0A0A0A] text-xs font-bold shrink-0">
                    Buy →
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Right action rail */}
      <div className="absolute right-2 sm:right-4 bottom-32 flex flex-col items-center gap-5 text-white">
        <button type="button" onClick={handleLike} data-testid={`plyx-like-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <Heart className={`w-8 h-8 ${liked ? "fill-[#FF2D7A] text-[#FF2D7A]" : ""}`}/>
          <span className="text-xs font-semibold mt-1">{fmtCount(likeCount)}</span>
        </button>
        <button type="button" onClick={() => onCommentClick?.(video)} data-testid={`plyx-comment-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <MessageCircle className="w-8 h-8"/>
          <span className="text-xs font-semibold mt-1">{fmtCount(video.comment_count)}</span>
        </button>
        <button type="button" onClick={handleShare} data-testid={`plyx-share-${video.video_id}`} className="flex flex-col items-center hover:scale-110 transition-transform">
          <Share2 className="w-8 h-8"/>
          <span className="text-xs font-semibold mt-1">{fmtCount(video.share_count)}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            if (!me) {
              toast.error("Sign in to send gifts");
              navigate("/plyx/login");
              return;
            }
            if (me?.plyx_user_id === video.creator_id) {
              toast.info("You can't gift your own video — share it instead!");
              return;
            }
            setGiftDrawerOpen(true);
          }}
          data-testid={`plyx-gift-btn-${video.video_id}`}
          className="flex flex-col items-center hover:scale-110 transition-transform"
        >
          <Gift className="w-8 h-8 text-[#FFB42D]" style={{ filter: "drop-shadow(0 0 6px rgba(255,180,45,0.4))" }} />
          <span className="text-xs font-semibold mt-1">{fmtCount(giftStats.total || 0)}</span>
        </button>
      </div>

      {/* Floating-emoji gift overlay — animates up & out */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid={`plyx-gift-stream-${video.video_id}`}>
        {floatingGifts.map((g) => (
          <span
            key={g.id}
            className="absolute"
            style={{
              left: `${g.lane}%`,
              bottom: 80,
              fontSize: g.size,
              animation: "plyxGiftRise 3s cubic-bezier(0.22,1,0.36,1) forwards",
              filter: "drop-shadow(0 0 10px rgba(255,180,45,0.6))",
            }}
          >
            {g.emoji}
          </span>
        ))}
      </div>

      {/* Whisper auto-caption line */}
      {captionsOn && captionLine && (
        <div
          className="absolute left-3 right-20 z-20 pointer-events-none"
          style={{ bottom: 110 }}
          data-testid={`plyx-caption-${video.video_id}`}
        >
          <div className="inline-block max-w-full px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur text-white text-[14px] leading-snug font-medium"
               style={{ textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
            {captionLine}
          </div>
        </div>
      )}

      {/* Caption toggle pill — bottom-left, only visible when captions exist */}
      {video.captions_status === "ready" && (
        <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCaptionsOn((c) => !c)}
            data-testid={`plyx-cc-toggle-${video.video_id}`}
            title={captionsOn ? "Turn captions off" : "Turn captions on"}
            className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider border transition-colors
                        ${captionsOn ? "bg-white text-[#0A0A0A] border-white" : "bg-black/40 text-white/70 border-white/30"}`}
          >
            CC
          </button>
          {captionsOn && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setLangMenuOpen((o) => !o)}
                data-testid={`plyx-cc-lang-btn-${video.video_id}`}
                title="Translate captions"
                className="px-2 py-1 rounded text-[10px] font-semibold bg-black/40 text-white/80 border border-white/30 hover:bg-black/60 inline-flex items-center gap-1"
              >
                <Languages className="w-3 h-3"/>{captionLang === "auto" ? "Original" : captionLang.toUpperCase()}
              </button>
              {langMenuOpen && (
                <div
                  className="absolute bottom-full mb-1 left-0 z-30 min-w-[140px] max-h-60 overflow-y-auto rounded-md bg-[#0A0A0A] border border-white/15 shadow-xl py-1"
                  data-testid={`plyx-cc-lang-menu-${video.video_id}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setCaptionLang("auto");
                      setTranslatedSegs(null);
                      setLangMenuOpen(false);
                    }}
                    data-testid={`plyx-cc-lang-opt-auto-${video.video_id}`}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/10
                                ${captionLang === "auto" ? "text-[#FFB42D]" : "text-white/90"}`}
                  >
                    Original
                  </button>
                  {[
                    {code:"en",label:"English"},{code:"fr",label:"French"},{code:"es",label:"Spanish"},
                    {code:"pt",label:"Portuguese"},{code:"ar",label:"Arabic"},{code:"sw",label:"Swahili"},
                    {code:"yo",label:"Yoruba"},{code:"ig",label:"Igbo"},{code:"ha",label:"Hausa"},
                    {code:"zu",label:"Zulu"},{code:"am",label:"Amharic"},{code:"zh",label:"Chinese"},
                    {code:"hi",label:"Hindi"},
                  ].map((L) => (
                    <button
                      key={L.code}
                      type="button"
                      onClick={async () => {
                        setLangMenuOpen(false);
                        if (captionLang === L.code && translatedSegs) return;
                        try {
                          toast.message(`Translating to ${L.label}…`, { duration: 1200 });
                          const r = await plyxApi.post(
                            `/plyx/videos/${video.video_id}/captions/translate`,
                            { lang: L.code },
                          );
                          if (r.data?.segments?.length) {
                            setTranslatedSegs(r.data.segments);
                            setCaptionLang(L.code);
                            toast.success(`Captions in ${L.label}${r.data.cached ? "" : " ✨"}`);
                          } else {
                            toast.error("No translation returned");
                          }
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Translation failed");
                        }
                      }}
                      data-testid={`plyx-cc-lang-opt-${L.code}-${video.video_id}`}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/10
                                  ${captionLang === L.code ? "text-[#FFB42D]" : "text-white/90"}`}
                    >
                      {L.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {video.captions_status === "generating" && (
        <div
          className="absolute bottom-4 left-4 z-20 px-2 py-1 rounded text-[10px] font-semibold tracking-wider bg-black/60 backdrop-blur text-white/80 border border-white/20"
          data-testid={`plyx-cc-pending-${video.video_id}`}
        >
          CAPTIONS…
        </div>
      )}

      {/* Gift drawer */}
      <GiftDrawer
        open={giftDrawerOpen}
        onClose={() => setGiftDrawerOpen(false)}
        videoId={video.video_id}
        onGiftSent={handleGiftSent}
      />

      <style>{`
        @keyframes plyxGiftRise {
          0%   { transform: translate(0, 0) rotate(-8deg) scale(0.6); opacity: 0; }
          15%  { transform: translate(0, -30px) rotate(-8deg) scale(1.15); opacity: 1; }
          70%  { transform: translate(0, -260px) rotate(8deg) scale(1); opacity: 1; }
          100% { transform: translate(0, -360px) rotate(0deg) scale(0.85); opacity: 0; }
        }
        @keyframes plyxRibbonIn {
          0%   { transform: translateY(-10px); opacity: 0; }
          100% { transform: translateY(0);     opacity: 1; }
        }
        @keyframes plyxPhotoKenBurns {
          0%   { transform: scale(1.0)  translate(0, 0); }
          100% { transform: scale(1.08) translate(-1.5%, 1.5%); }
        }
      `}</style>

      {/* Bottom scrubber — thin progress line, TikTok-style. Only visible while active. */}
      {isActive && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20 pointer-events-none"
          data-testid={`plyx-scrubber-${video.video_id}`}
        >
          <div
            className="h-full bg-white/90 transition-[width] duration-150 ease-linear"
            style={{ width: `${(progress * 100).toFixed(2)}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default VideoCard;
